import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import { ThemeProvider, styled } from '@mui/material/styles';
import theme, { timeTrackerReportTheme } from '../../app/theme';
import { PageProps } from '../../types/PageProps';
import proemLogo from '../../assets/images/proem-logo.png';
import { HttpStatusCode } from '../../common/utils/httpStatusCode';
import ErrorPage from '../error/ErrorPage';
import { useGetTimeTrackerReportByTokenRequestQuery } from '../../app/services/interview';
import dayjs from 'dayjs';
import { TimeTrackerReportItemResponseDto } from '../../types/TimeTracker';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

const borderStyle = `1px solid ${theme.xPalette.black}`;
const StyleTitleSection = styled(Typography)(() => ({
  fontSize: '20px',
}));

export default function TimeTrackerReportContentPage({ breadcrumbs }: PageProps) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams?.get('token') || null;
  const currentStartDate = dayjs().startOf("month").format("MM/DD/YYYY");
  const currentEndDate = dayjs().endOf("month").format("MM/DD/YYYY");
  const fromDate = searchParams?.get('fromDate') || currentStartDate;
  const toDate = searchParams?.get('toDate') || currentEndDate;

  const { data: timeTrackerReportData, error } = useGetTimeTrackerReportByTokenRequestQuery(
    token ? { token } : skipToken
  );

  if (!token || error) {
    return <ErrorPage statusCode={HttpStatusCode.NotFound} />;
  }

  const renderLogo = () => {
    if (timeTrackerReportData?.logoUrl) {
      return <img src={timeTrackerReportData?.logoUrl} width="220px" alt="Company Logo" />;
    } else {
      return <img src={proemLogo} width="220px" alt="Proem Logo" />;
    }
  };
  const renderColumn = (colName: string) => {
    return (<TableCell
      key={colName}
      sx={{
        border: borderStyle,
        backgroundColor: theme.xPalette.bodyBackground,
        width: colName === t('timeTracker.report.headers.patientName') ? 120 : 'auto',
        padding: '2px'
      }}
    >
      <Typography
        fontWeight={'bold'}
        fontSize={'12px'}
        dangerouslySetInnerHTML={{
          __html: colName,
        }}
      ></Typography>
    </TableCell>
    )
  }
  const renderCellData = (value: any, nowrap?: boolean) => {
    return <TableCell
      sx={{
        fontSize: 12,
        border: borderStyle,
        whiteSpace: nowrap ? 'nowrap' : 'normal',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        padding: '2px'
      }}
    >
      {Array.isArray(value) ? (
        value.join(', ')
      ) : (
        value || ''
      )}
    </TableCell>
  }
  const renderDataRow = (timeTrackerReportItems: TimeTrackerReportItemResponseDto[]) => {
    return (
      timeTrackerReportItems?.map((item, rowIdx: number) => (
        <TableRow key={rowIdx}>
          {renderCellData(item.id)}
          {renderCellData(item.patientName)}
          {renderCellData(item.dob, true)}
          {renderCellData(item.icd10Primary)}
          {renderCellData(item.icd10Secondary)}
          {renderCellData(item.service)}
          {renderCellData(item.noteType)}
          {renderCellData(item.timeStamp, true)}
          {renderCellData(item.createBy)}
          {renderCellData(item.accountName)}
          {renderCellData(item.siteName)}
          {renderCellData(item.provider)}
          {renderCellData(item.minutes)}
          {renderCellData(item.cptCode)}

        </TableRow>
      )));
  }

  const renderTimeTrackerTable = (timeTrackerReportItems: TimeTrackerReportItemResponseDto[]) => {
    return (
      <Table sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            {renderColumn(t('timeTracker.report.headers.Id'))}
            {renderColumn(t('timeTracker.report.headers.patientName'))}
            {renderColumn(t('timeTracker.report.headers.dob'))}
            {renderColumn(t('timeTracker.report.headers.icd10Primary'))}
            {renderColumn(t('timeTracker.report.headers.icd10Secondary'))}
            {renderColumn(t('timeTracker.report.headers.service'))}
            {renderColumn(t('timeTracker.report.headers.nodeType'))}
            {renderColumn(t('timeTracker.report.headers.timeStamp'))}
            {renderColumn(t('timeTracker.report.headers.createBy'))}
            {renderColumn(t('timeTracker.report.headers.practice'))}
            {renderColumn(t('timeTracker.report.headers.facility'))}
            {renderColumn(t('timeTracker.report.headers.provider'))}
            {renderColumn(t('timeTracker.report.headers.timeSpent'))}
            {renderColumn(t('timeTracker.report.headers.cptCodes'))}
          </TableRow>
        </TableHead>
        <TableBody>
          {renderDataRow(timeTrackerReportItems)}
        </TableBody>
      </Table>
    );
  };

  return (
    <ThemeProvider theme={timeTrackerReportTheme} >
      <CssBaseline />
      <Container sx={{ width: '100%', paddingLeft: 0, paddingRight: 0 }} disableGutters={true}>
        <Box marginTop="30px">{renderLogo()}</Box>
        <Box marginTop={1} sx={{ display: 'flex', justifyContent: 'center' }}>
          <StyleTitleSection display="flex">
            <StyleTitleSection fontWeight={700}>
              {`${t('timeTracker.report.title')}: ${fromDate} - ${toDate}`}
            </StyleTitleSection>
          </StyleTitleSection>
        </Box>
        <Box marginY={2}>
          {(timeTrackerReportData?.timeTrackerReportItems?.length ?? 0) > 0
            ? (renderTimeTrackerTable(timeTrackerReportData?.timeTrackerReportItems ?? []))
            : <>{`${t('timeTracker.error.noDataFound')}`}</>}
        </Box>
      </Container>
    </ThemeProvider>
  );
}
